import { useState, useCallback } from 'react';
import { NotebookFolder, NotebookPage } from '../pages/NotebookPage';

export const useNotebook = () => {
  const [folders, setFolders] = useState<NotebookFolder[]>([]);
  const [pages, setPages] = useState<NotebookPage[]>([]);
  const [selectedFolder, setSelectedFolder] = useState<NotebookFolder | null>(null);
  const [selectedPage, setSelectedPage] = useState<NotebookPage | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadFolders = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch('/api/notebook/folders', {
        method: 'GET',
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to load folders');
      }

      const data = await response.json();
      setFolders(data.folders || []);
      
      // Auto-select first folder if none selected
      if (data.folders?.length > 0 && !selectedFolder) {
        setSelectedFolder(data.folders[0]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load folders');
      console.error('Failed to load folders:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedFolder]);

  const loadPages = useCallback(async (folderId: string) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`/api/notebook/pages?folderId=${folderId}`, {
        method: 'GET',
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to load pages');
      }

      const data = await response.json();
      setPages(data.pages || []);
      
      // Clear selected page if it's not in the new folder
      if (selectedPage && !data.pages?.find((p: NotebookPage) => p.id === selectedPage.id)) {
        setSelectedPage(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load pages');
      console.error('Failed to load pages:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedPage]);

  const createFolder = useCallback(async (folderData: {
    name: string;
    description?: string;
    color?: string;
  }) => {
    try {
      setError(null);
      
      const response = await fetch('/api/notebook/folders', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(folderData),
      });

      if (!response.ok) {
        throw new Error('Failed to create folder');
      }

      const data = await response.json();
      const newFolder = data.folder;
      
      setFolders(prev => [...prev, newFolder]);
      setSelectedFolder(newFolder);
      
      return newFolder;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create folder');
      console.error('Failed to create folder:', err);
      throw err;
    }
  }, []);

  const createPage = useCallback(async (pageData: {
    folderId: string;
    title: string;
    content?: string;
    contentType?: string;
  }) => {
    try {
      setError(null);
      
      const response = await fetch('/api/notebook/pages', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(pageData),
      });

      if (!response.ok) {
        throw new Error('Failed to create page');
      }

      const data = await response.json();
      const newPage = data.page;
      
      setPages(prev => [newPage, ...prev]);
      
      return newPage;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create page');
      console.error('Failed to create page:', err);
      throw err;
    }
  }, []);

  const updatePage = async (pageData: Partial<NotebookPage>) => {
    try {
      setLoading(true);
      const response = await fetch('/api/notebook/pages', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(pageData),
      });

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error || 'Failed to update page');
      }

      // Update the page in the local state
      setPages(prev => prev.map(page => 
        page.id === result.page.id ? result.page : page
      ));

      return result.page;
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to update page');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const updateFolder = async (folderData: Partial<NotebookFolder>) => {
    try {
      setLoading(true);
      const response = await fetch('/api/notebook/folders', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(folderData),
      });

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error || 'Failed to update folder');
      }

      // Update the folder in the local state
      setFolders(prev => prev.map(folder => 
        folder.id === result.folder.id ? result.folder : folder
      ));

      return result.folder;
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to update folder');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const deleteFolder = async (folderId: string) => {
    try {
      setLoading(true);
      const response = await fetch(`/api/notebook/folders?id=${folderId}`, {
        method: 'DELETE',
      });

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error || 'Failed to delete folder');
      }

      // Remove the folder from local state
      setFolders(prev => prev.filter(folder => folder.id !== folderId));
      
      // If the deleted folder was selected, clear selection
      if (selectedFolder?.id === folderId) {
        setSelectedFolder(null);
        setSelectedPage(null);
        setPages([]);
      }

      return true;
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to delete folder');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const deletePage = async (pageId: string) => {
    try {
      setLoading(true);
      const response = await fetch(`/api/notebook/pages?id=${pageId}`, {
        method: 'DELETE',
      });

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error || 'Failed to delete page');
      }

      // Remove the page from local state
      setPages(prev => prev.filter(page => page.id !== pageId));
      
      // If the deleted page was selected, clear selection
      if (selectedPage?.id === pageId) {
        setSelectedPage(null);
      }

      return true;
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to delete page');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const selectFolder = useCallback((folder: NotebookFolder) => {
    setSelectedFolder(folder);
    setSelectedPage(null); // Clear selected page when switching folders
  }, []);

  const selectPage = useCallback((page: NotebookPage) => {
    setSelectedPage(page);
  }, []);

  return {
    folders,
    pages,
    selectedFolder,
    selectedPage,
    loading,
    error,
    loadFolders,
    loadPages,
    createFolder,
    createPage,
    updatePage,
    updateFolder,
    deleteFolder,
    deletePage,
    selectFolder,
    selectPage,
  };
}; 